/**
 * usePerformancePermissions Hook
 * Determines what performance views are accessible based on user role
 */

import { useAuth } from '../context/AuthContext';
import type { PerformancePermissions } from '../types';

/**
 * Hook to get performance permissions based on user role
 */
export function usePerformancePermissions(): PerformancePermissions {
  const { user } = useAuth();
  const role = user?.role || '';

  // Define permissions based on role
  const permissions: PerformancePermissions = {
    canViewOwn: false,
    canViewTeam: false,
    canViewOrg: false,
    canExport: false,
    canConfigureWeights: false,
  };

  switch (role) {
    case 'super_admin':
      // Super admin has full access (for support purposes)
      permissions.canViewOwn = true;
      permissions.canViewTeam = true;
      permissions.canViewOrg = true;
      permissions.canExport = true;
      permissions.canConfigureWeights = true;
      break;

    case 'account_admin':
      // Account admin has full organization access
      permissions.canViewOwn = true;
      permissions.canViewTeam = true;
      permissions.canViewOrg = true;
      permissions.canExport = true;
      permissions.canConfigureWeights = true;
      break;

    case 'manager':
      // Manager can view own and team performance
      permissions.canViewOwn = true;
      permissions.canViewTeam = true;
      permissions.canViewOrg = false;
      permissions.canExport = true;
      permissions.canConfigureWeights = false;
      break;

    case 'employee':
      // Employee can only view own performance
      permissions.canViewOwn = true;
      permissions.canViewTeam = false;
      permissions.canViewOrg = false;
      permissions.canExport = true; // Can export own report
      permissions.canConfigureWeights = false;
      break;

    case 'viewer':
      // Viewer has no performance access
      permissions.canViewOwn = false;
      permissions.canViewTeam = false;
      permissions.canViewOrg = false;
      permissions.canExport = false;
      permissions.canConfigureWeights = false;
      break;

    default:
      // Default to no access
      break;
  }

  return permissions;
}

/**
 * Hook to check if user can access a specific performance view
 */
export function useCanAccessPerformanceView(view: 'own' | 'team' | 'org'): boolean {
  const permissions = usePerformancePermissions();

  switch (view) {
    case 'own':
      return permissions.canViewOwn;
    case 'team':
      return permissions.canViewTeam;
    case 'org':
      return permissions.canViewOrg;
    default:
      return false;
  }
}

/**
 * Hook to get available tabs for performance page based on permissions
 */
export function usePerformanceTabs(): Array<{ id: string; label: string; enabled: boolean }> {
  const permissions = usePerformancePermissions();

  return [
    {
      id: 'my-performance',
      label: 'My Performance',
      enabled: permissions.canViewOwn,
    },
    {
      id: 'team-performance',
      label: 'Team Performance',
      enabled: permissions.canViewTeam,
    },
    {
      id: 'organization',
      label: 'Organization',
      enabled: permissions.canViewOrg,
    },
  ].filter((tab) => tab.enabled);
}
